import { Box, Stack } from '@mantine/core';
import { SettingsSwitch } from '@repo/common/components';
import type { PreferenceOption } from '@repo/common/constants/integration/integrations.preferences';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback } from 'react';
import {
	integrationsQueryKeyFactory,
	queryClient,
	useUpdateIntegration,
	type IIntegration,
} from '../../../../../api';
import type { WithOnlyIdRequired } from '../../../../../lib/typescript';
import { openConfirmModal } from '../../../../ModalManager';

interface GenericOptionsProps {
	integration: IIntegration;
	options: PreferenceOption[];
	title: string;
	description?: string;
	withConfirmation?: boolean;
}

export const GenericOptionsSwitches = memo(
	({
		integration,
		options,
		title,
		description,
		withConfirmation,
	}: GenericOptionsProps) => {
		const { mutateAsync: updateIntegration } = useUpdateIntegration({});

		const getCheckedValue = (option: PreferenceOption) => {
			// Check credentialFieldName first (takes precedence)
			if (option.credentialFieldName) {
				return Boolean(integration?.credentials?.[option.credentialFieldName]);
			}

			// Check integrationFieldName if no credentialFieldName
			if (
				option.integrationFieldName &&
				integration?.[option.integrationFieldName as keyof IIntegration]
			) {
				return Boolean(
					(integration as unknown as Record<string, unknown>)[
						option.integrationFieldName
					]
				);
			}

			// Default to false if neither field is found
			return false;
		};

		const handlePreferenceChange = useCallback(
			(
				checked: boolean | string,
				integrationFieldName?: string,
				credentialFieldName?: string
			) => {
				const data: WithOnlyIdRequired<IIntegration> = {
					id: integration.id,
				};

				if (credentialFieldName) {
					data.credentials = {
						...(integration?.credentials ?? {}),
						[credentialFieldName]: checked,
					};
				}
				if (integrationFieldName) {
					data[integrationFieldName as keyof WithOnlyIdRequired<IIntegration>] =
						checked as never;
				}
				updateIntegration(
					{
						data,
					},
					{
						onSuccess: () => {
							queryClient.invalidateQueries({
								queryKey: integrationsQueryKeyFactory.byId(integration.id),
							});
						},
					}
				);
			},
			[updateIntegration, integration]
		);

		const handlePreferenceChangeWithConfirm = useCallback(
			(
				checked: boolean | string,
				integrationFieldName?: string,
				credentialFieldName?: string,
				optionTitle?: string
			) => {
				openConfirmModal({
					title: 'Are you sure?',
					children: `Are you sure you want to change the "${optionTitle}" preference? This may irreversibly overwrite properties.`,
					onConfirm: () => {
						handlePreferenceChange(
							checked,
							integrationFieldName,
							credentialFieldName
						);
					},
				});
			},
			[handlePreferenceChange]
		);

		return (
			<Stack spacing="md">
				<Stack spacing={0}>
					<Title size="md">{title}</Title>
					{description && (
						<Text size="sm" color="text/secondary/default">
							{description}
						</Text>
					)}
				</Stack>
				<Box>
					{options.map((option) => (
						<SettingsSwitch
							key={option.credentialFieldName ?? option.integrationFieldName}
							title={option.title}
							description={option.description}
							checked={getCheckedValue(option)}
							onChange={(checked) => {
								if (checked && withConfirmation) {
									handlePreferenceChangeWithConfirm(
										checked,
										option.integrationFieldName,
										option.credentialFieldName,
										option.title
									);
								} else {
									handlePreferenceChange(
										checked,
										option.integrationFieldName,
										option.credentialFieldName
									);
								}
							}}
						/>
					))}
				</Box>
			</Stack>
		);
	}
);
